import { Component, Input, OnInit } from '@angular/core';
import { WorkOrderDownloadMenuConfiguration, WorkOrderRowsExportTypeVO, LanguageOption, ExportTemplateOption } from './work-order-download-menu.configuration';
import { CtModelService, CTModelDatatableFilter, CtBinaryOperator, CtModelConfiguration, CTDatatablePaginationParameter, CtModelDatatableOperators } from '@ctsolution/ct-framework';
import { WorkOrderFileRouteData } from '../../_core/route-data/work-order-file.route-data';
import { CtWebapiGenericResponse } from '@ctsolution/ct-webapi';
import { WorkOrderService } from '../work-order/work-order.service';
import { ReportDownloadParameter } from '../../_core/classes/report-download.parameter';
import { FileService } from '../../_core/lib/file.service';
import { FileGet } from '../../_core/classes/file-get';

/**
 * Interfaccia per rappresentare un elemento nel menu di download
 */
interface DownloadMenuItem {
  /** Nome del file da mostrare nel menu */
  fileName: string;
  /** Tipo di elemento: 'generated' per PDF generato, 'uploaded' per file caricato */
  type: 'generated' | 'uploaded';
  /** Dati della lingua (solo per PDF generato con lingua) */
  languageData?: LanguageOption;
  /** Dati del file (solo per file caricati) */
  fileData?: WorkOrderFileData;
  /** Indica se questo è il separatore visivo */
  isSeparator?: boolean;
}

/**
 * Interfaccia per i dati del WorkOrderFile recuperati dal backend
 */
interface WorkOrderFileData {
  Oid: number;
  FileName: string;
  CreateAt: Date;
  FileOrigin: string;
}

/**
 * Componente per gestire il menu di download dei WorkOrder
 *
 * Questo componente mostra un menu a tendina con:
 * - Il PDF generato automaticamente dal sistema (sempre in cima)
 * - I documenti caricati manualmente nella tab "Gestione Documenti" (max 5, più recenti)
 *
 * Il componente può essere configurato tramite WorkOrderDownloadMenuConfiguration
 * e può essere disabilitato tramite override per tornare al comportamento legacy
 */
@Component({
  selector: 'app-work-order-download-menu',
  templateUrl: './work-order-download-menu.component.html',
  styleUrls: ['./work-order-download-menu.component.scss']
})
export class WorkOrderDownloadMenuComponent implements OnInit {

  /**
   * Configurazione del componente
   * Può essere sovrascritta tramite il sistema di override del CtManagerService
   */
  @Input() configuration: WorkOrderDownloadMenuConfiguration | null = null;

  /**
   * OID del WorkOrder per cui mostrare i documenti
   * Obbligatorio per caricare i file dalla tab Gestione Documenti
   */
  @Input() workOrderOid: number | null = null;

  /**
   * Record completo del WorkOrder, necessario solo quando groupByDocumentType = true
   * per decidere se mostrare il gruppo "Conferma d'Ordine".
   * Una commessa è considerata "firmata/standard" quando QuotationAcceptAt != null
   * oppure Quotation === false (criterio coerente con work-order-list.navigateToEdit).
   */
  @Input() workOrderData: any | null = null;

  /**
   * Lista degli elementi da mostrare nel menu
   * Include il PDF generato e i file caricati
   */
  menuItems: DownloadMenuItem[] = [];

  /**
   * Flag per indicare se i dati sono in caricamento
   */
  isLoading: boolean = false;

  /**
   * Flag per indicare se si è verificato un errore durante il caricamento
   */
  hasError: boolean = false;

  /**
   * Flag per indicare se i file sono già stati caricati
   * Usato per implementare lazy loading e cache
   * Pubblico perché usato nel template per lo spinner
   */
  filesLoaded: boolean = false;

  /**
   * Indica se il record corrente è una commessa firmata o standard.
   * - true  -> QuotationAcceptAt valorizzato OPPURE Quotation === false
   * - false -> offerta non ancora firmata
   * Usato per decidere se mostrare il gruppo "Conferma d'Ordine".
   */
  get isSignedOrStandard(): boolean {
    if (!this.workOrderData) return false;
    return this.workOrderData.QuotationAcceptAt != null || this.workOrderData.Quotation === false;
  }

  /**
   * Lingue da mostrare nei sotto-menu (usate sia per Preventivo che per Conferma d'Ordine).
   * In modalità raggruppata l'elenco delle lingue è condiviso: ciò che cambia è solo
   * il flag inviato al backend al momento del click (vedi onGroupedLanguageClick).
   */
  get groupedLanguages(): LanguageOption[] {
    return this.configuration?.languages ?? [];
  }
    get exportTemplates(): ExportTemplateOption[] {
    return this.configuration?.exportTemplates ?? [];
  }

  get hasExportTemplates(): boolean {
    return this.exportTemplates.length > 0;
  }

  constructor(
    private modelService: CtModelService<any>,
    private workOrderService: WorkOrderService,
    private fileService: FileService
  ) {}

  ngOnInit(): void {
    // Validazione dell'input
    if (!this.configuration) {
      console.error('WorkOrderDownloadMenuComponent: configuration è obbligatorio');
      return;
    }

    if (!this.workOrderOid) {
      console.error('WorkOrderDownloadMenuComponent: workOrderOid è obbligatorio');
      return;
    }

    // ✅ OTTIMIZZAZIONE: Non caricare più i documenti all'init
    // I documenti verranno caricati solo quando l'utente apre il menu (lazy loading)
    // Questo risolve il problema della pagina 2+ e migliora le performance
    this.initializeLanguageItems();
  }

  /**
   * Inizializza solo gli elementi delle lingue per il PDF generato
   * I file caricati verranno aggiunti al primo click sul bottone
   */
  private initializeLanguageItems(): void {
    this.menuItems = [];

    // Aggiungi le lingue disponibili per il PDF generato come primi elementi
    if (this.configuration?.languages && this.configuration.languages.length > 0) {
      this.configuration.languages.forEach(lang => {
        this.menuItems.push({
          fileName: lang.language, // Es: "Italiano"
          type: 'generated',
          languageData: lang // Dati della lingua per il download
        });
      });
    } else {
      // Fallback: aggiungi elemento generico se non ci sono lingue configurate
      this.menuItems.push({
        fileName: this.configuration?.generatedPdfLabel || 'PDF Generato Automaticamente',
        type: 'generated'
      });
    }
  }

  /**
   * ✅ NUOVO METODO
   * Gestisce l'apertura del menu
   * Carica i file dalla tab Gestione Documenti solo al primo click (lazy loading + cache)
   */
  onMenuOpened(): void {
    if (this.hasExportTemplates) return; // modalità export template: niente file caricati
    if (this.filesLoaded) return;
    if (this.workOrderOid) this.loadUploadedFiles();
  }

  /**
   * ✅ NUOVO METODO
   * Carica solo i file caricati dalla tab Gestione Documenti
   * Aggiunge i file agli elementi del menu già esistenti (lingue PDF)
   */
  private async loadUploadedFiles(): Promise<void> {
    this.isLoading = true;
    this.hasError = false;

    try {
      // Recupera i file dal backend
      const uploadedFiles = await this.fetchWorkOrderFiles(this.workOrderOid!);

      // Se ci sono file caricati, aggiungi un separatore e i file
      if (uploadedFiles.length > 0) {
        // Aggiungi separatore
        this.menuItems.push({
          fileName: this.configuration?.uploadedDocumentsLabel || 'Documenti Caricati',
          type: 'uploaded',
          isSeparator: true
        });

        // Aggiungi i file
        this.menuItems.push(...uploadedFiles);
      }

      // Marca i file come caricati per non ricaricarli al prossimo click
      this.filesLoaded = true;

    } catch (error) {
      console.error('WorkOrderDownloadMenuComponent: Errore durante il caricamento dei documenti', error);
      this.hasError = true;
      // Non marcare come filesLoaded in caso di errore, così si riprova al prossimo click
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * ❌ DEPRECATO - Metodo rimosso
   * La logica è stata suddivisa in:
   * - initializeLanguageItems() per l'init
   * - loadUploadedFiles() per il lazy loading al click
   */

  /**
   * Recupera i WorkOrderFile dal backend
   * @param workOrderOid - OID del WorkOrder
   * @returns Lista di DownloadMenuItem per i file caricati
   */
  private async fetchWorkOrderFiles(workOrderOid: number): Promise<DownloadMenuItem[]> {
    return new Promise((resolve, reject) => {
      // Configurazione per recuperare i WorkOrderFile
      const config = CtModelConfiguration
        .create()
        .setRouteData(WorkOrderFileRouteData());

      // Filtro per recuperare solo i file del WorkOrder corrente
      const filter = CTModelDatatableFilter
        .create()
        .setField('WorkOrder.Oid')
        .setValue(workOrderOid)
        .setOperatorType(CtBinaryOperator.Equal);

      // Paginazione per ottenere solo i file più recenti
      // IMPORTANTE: L'ordinamento deve essere gestito tramite il backend
      // Se il backend supporta l'ordinamento tramite parametri, aggiungerli qui
      const pagination = new CTDatatablePaginationParameter(
        1, // Pagina 1
        this.configuration?.maxDocumentsToShow || 5 // Numero di elementi da recuperare
      );

      // Crea gli operators con filtri e paginazione
      const operators = CtModelDatatableOperators
        .create()
        .setFilters([filter])
        .setPagination(pagination);

      this.modelService
        .getList(config, operators)
        .subscribe({
          next: (response: CtWebapiGenericResponse<any>) => {
            const files: DownloadMenuItem[] = [];

            if (response?.Result?.DataSource && Array.isArray(response.Result.DataSource)) {
              // Ordina i file per CreateAt decrescente (più recenti prima) lato client
              // per garantire che vengano mostrati nell'ordine corretto
              const sortedFiles = response.Result.DataSource.sort((a: any, b: any) => {
                const dateA = a.CreateAt ? new Date(a.CreateAt).getTime() : 0;
                const dateB = b.CreateAt ? new Date(b.CreateAt).getTime() : 0;
                return dateB - dateA; // Ordine decrescente (più recenti prima)
              });

              // Mappa i risultati in DownloadMenuItem
              sortedFiles.forEach((file: any) => {
                files.push({
                  fileName: file.FileName || file.Name || 'Documento senza nome',
                  type: 'uploaded',
                  fileData: {
                    Oid: file.Oid,
                    FileName: file.FileName || file.Name,
                    CreateAt: file.CreateAt,
                    FileOrigin: file.FileOrigin || 'WorkOrderFile'
                  }
                });
              });
            }

            resolve(files);
          },
          error: (error) => {
            console.error('WorkOrderDownloadMenuComponent: Errore durante il recupero dei WorkOrderFile', error);
            reject(error);
          }
        });
    });
  }

  /**
   * Gestisce il click su un elemento del menu
   * Scarica il PDF generato o il file caricato in base al tipo
   */
  onMenuItemClick(item: DownloadMenuItem): void {
    // Non fare nulla se è un separatore
    if (item.isSeparator) return;

    if (item.type === 'generated') {
      // Modalità flat (legacy): nessun forceQuotation, comportamento storico invariato.
      this.downloadGeneratedPdf(item.languageData);
    } else if (item.type === 'uploaded' && item.fileData) {
      // Scarica il file caricato
      this.downloadUploadedFile(item.fileData);
    }
  }

  /**
   * Handler dedicato al click su una lingua dentro un sotto-menu raggruppato.
   * Il flag forceQuotation va a true SOLO quando l'utente apre il sotto-menu
   * "Preventivo" partendo da un record già firmato/standard: in quel caso
   * il backend deve essere forzato a generare il PDF di preventivo tramite
   * il query param Quotation=true.
   * In tutti gli altri casi (offerta non firmata che già produce di default
   * il preventivo, oppure click su Conferma d'Ordine) NON si forza nulla
   * e si mantiene la logica backend preesistente.
   */
  onGroupedLanguageClick(language: LanguageOption, forceQuotation: boolean): void {
    this.downloadGeneratedPdf(language, forceQuotation);
  }

  /**
   * Scarica il PDF generato automaticamente dal sistema
   * @param language - Opzione lingua selezionata (opzionale, usa default se non specificato)
   * @param forceQuotation - Se true, aggiunge il query param `Quotation=true` alla request
   *                         per forzare il backend a generare il PDF in versione Preventivo.
   *                         Default false: comportamento storico invariato.
   */
  private downloadGeneratedPdf(language?: LanguageOption, forceQuotation: boolean = false): void {
    if (!this.workOrderOid) {
      console.error('WorkOrderDownloadMenuComponent: impossibile scaricare il PDF, workOrderOid non definito');
      return;
    }

    // Prepara i parametri per il download del PDF generato
    // Usa la lingua selezionata o la prima lingua configurata come fallback
    const languageCode = language?.code || this.configuration?.languages?.[0]?.code || 'it';
    const rowType = language?.rowType || this.configuration?.languages?.[0]?.rowType || WorkOrderRowsExportTypeVO.All;

    const parameter: ReportDownloadParameter = ReportDownloadParameter
      .create()
      .setWorkOrderOid(this.workOrderOid)
      .setTemplateLanguage(languageCode)
      .setRowType(rowType)
      .setDirectDownload(false)
      .setOpenInBrowser(true)
      .setAction('WorkOrderPdf')
      .setLabelDownload(`WorkOrder${this.workOrderOid}`)
      // Quotation viene forzato a true SOLO quando l'utente clicca
      // "Preventivo" su un record già firmato/standard. Negli altri casi
      // resta null e il query param non viene inviato (comportamento legacy).
      .setQuotation(forceQuotation ? true : null);

    // Usa il service del WorkOrder per il download
    this.workOrderService.downloadWorkOrderPDF(parameter);
  }
onExportTemplateClick(template: ExportTemplateOption): void {
    if (!this.workOrderOid) {
      console.error('WorkOrderDownloadMenuComponent: workOrderOid non definito');
      return;
    }

    const parameter: ReportDownloadParameter = ReportDownloadParameter
      .create()
      .setWorkOrderOid(this.workOrderOid)
      .setTemplateLanguage(template.languageCode ?? this.configuration?.languages?.[0]?.code ?? 'it')
      .setRowType(template.rowType ?? WorkOrderRowsExportTypeVO.All)
      .setDirectDownload(false)
      .setOpenInBrowser(true)
      .setAction('WorkOrderPdf')
      .setLabelDownload(`WorkOrder${this.workOrderOid}`)
      .setExportCustomTemplateType(template.exportCustomTemplateType);

    this.workOrderService.downloadWorkOrderPDF(parameter);
  }
  /**
   * Scarica un file caricato dalla tab Gestione Documenti
   */
  private downloadUploadedFile(fileData: WorkOrderFileData): void {
    if (!fileData.Oid) {
      console.error('WorkOrderDownloadMenuComponent: impossibile scaricare il file, Oid non definito');
      return;
    }

    // Costruisce l'endpoint per il download del file
    // Utilizza lo stesso pattern del WorkOrderFileController
    const endpoint = `/ct/File/Get?id=${fileData.Oid}`;

    const fileGet: FileGet = {
      Endpoint: endpoint
    };

    // Scarica il file utilizzando il FileService
    // Passa il nome del file per il download
    this.fileService.downloadAttachmentByEndPoint(fileGet, fileData.FileName);
  }

  /**
   * Determina l'icona da mostrare in base al tipo di file
   * Usa le Material Icons
   */
  getFileIcon(item: DownloadMenuItem): string {
    if (item.type === 'generated') {
      return 'picture_as_pdf'; // Icona per PDF generato
    }

    // Per i file caricati, cerca di determinare l'icona dal nome del file
    if (item.fileData?.FileName) {
      const fileName = item.fileData.FileName.toLowerCase();

      if (fileName.endsWith('.pdf')) return 'picture_as_pdf';
      if (fileName.endsWith('.doc') || fileName.endsWith('.docx')) return 'description';
      if (fileName.endsWith('.xls') || fileName.endsWith('.xlsx')) return 'table_chart';
      if (fileName.endsWith('.jpg') || fileName.endsWith('.jpeg') || fileName.endsWith('.png')) return 'image';
      if (fileName.endsWith('.zip') || fileName.endsWith('.rar')) return 'folder_zip';
    }

    // Icona di default per file generici
    return 'insert_drive_file';
  }

  /**
   * Determina la classe CSS per la bandiera della lingua
   * Usa flag-icon-css
   */
  getLanguageFlagClass(item: DownloadMenuItem): string {
    if (item.type === 'generated' && item.languageData?.icon) {
      return `flag-icon flag-icon-${item.languageData.icon}`;
    }
    return '';
  }
}
